import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { BarChart } from '../../components/shared/Charts/BarChart/BarChart';
import SupplierControls from '../app-add-supplier/supplier-controls';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { ReactComponent as InfoIcon } from '../../assets/svg/InfoIconLight.svg';
import { ServiceSalesTool } from '../../services/service-sales-tool';
import { UNIT } from '../page-dashboard-V2/helper';
import { parseAmount } from '../../util';
import './page-sales-tool.scss';

// --- Syna XML response parsers ---
// Syna returns XML converted to JSON with _text/_attributes patterns.
// These parsers extract the relevant fields for display.
const getText = (obj: any) => obj?._text || obj?.text || obj || '';

export const parseSynaCompanyData = (data: any) => {
  if (!data?.companyData) return null;
  try {
    const omfragad = data.companyData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return null;
    const addrList = omfragad?.Adresslista?.Adress;
    const addr = Array.isArray(addrList) ? addrList[0] : addrList;
    const bolsate = getText(omfragad?.Bolsate);
    return {
      name: getText(omfragad?.Namnlista?.Namn),
      address: getText(addr?.Gatabox),
      postcode: getText(addr?.Postnr),
      city: getText(addr?.Postort),
      kommun: bolsate ? bolsate.split('/')[0].trim() : '',
      ftgform: getText(omfragad?.Ftgform),
      regdatum: getText(omfragad?.Regdatum),
      orgNumber: omfragad?._attributes?.id || '',
      bransch: (() => {
        const bl = omfragad?.Branschlista?.Bransch;
        if (!bl) return '';
        const first = Array.isArray(bl) ? bl[0] : bl;
        return getText(first?.Branschtext);
      })(),
      board: [] as { name: string; role: string }[], // filled from boardData separately
    };
  } catch {
    return null;
  }
};

export const parseSynaRating = (data: any) => {
  if (!data?.ratingData) return null;
  try {
    const omfragad = data.ratingData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return null;
    const klass = omfragad?.Bedomning?.Klassning?.Klass;
    return {
      rating: getText(klass),
      ratingText: klass?._attributes?.not || '',
      limit: getText(omfragad?.Limit),
    };
  } catch {
    return null;
  }
};

export const parseSynaBoardData = (data: any): { name: string; role: string }[] => {
  try {
    const omfragad = data?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return [];
    const ledamotList = omfragad?.Ledningslista?.Ledamot;
    if (!ledamotList) return [];
    const list = Array.isArray(ledamotList) ? ledamotList : [ledamotList];
    return list.map((l: any) => {
      const funcs = l?.Funktionslista?.Funktion;
      let role = '';
      if (Array.isArray(funcs)) {
        role = funcs.map((f: any) => f?._attributes?.klartext || '').filter(Boolean).join(', ');
      } else {
        role = funcs?._attributes?.klartext || '';
      }
      // Shorten common role names
      role = role.replace(/Verkställande direktör/gi, 'VD');
      return {
        name: getText(l?.Lednamn) || '',
        role,
      };
    }).filter((m: any) => {
      // Exclude auditors (revisorer) — only show board members and executives
      if (!m.name) return false;
      return !m.role.toLowerCase().includes('revisor');
    });
  } catch {
    return [];
  }
};

export interface KPIYear {
  period: string;
  omsattning: number;
  resultat: number;
  balansomslutning: number;
  egetKapital: number;
  soliditet: string;
  anstallda: number;
  kassalikviditet: string;
}

/** Parse Swedish-formatted numbers (e.g. "1 234,56" or "*" for missing data) */
const parseSwedishNumber = (val: string | undefined): number => {
  if (!val || val === '*') return 0;
  return parseAmount(val);
};

export const parseSynaKPIs = (data: any): KPIYear[] => {
  try {
    const omfragad = data?.kpiData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return [];
    const nyckeltal = omfragad?.Nyckeltalslista?.Nyckeltal;
    if (!nyckeltal) return [];
    const list = Array.isArray(nyckeltal) ? nyckeltal : [nyckeltal];
    return list.map((n: any) => {
      const periodFrom = n?.Period_fr?._text || '';
      const periodTo = n?.Period_to?._text || '';
      const year = periodTo ? `20${periodTo.substring(0, 2)}` : '';
      const month = periodTo ? periodTo.substring(2) : '';
      return {
        period: year && month ? `${year}-${month}` : periodFrom + '-' + periodTo,
        omsattning: parseSwedishNumber(n?.Oms?._text),
        resultat: parseSwedishNumber(n?.Res?._text),
        balansomslutning: parseSwedishNumber(n?.BalOmsl?._text),
        egetKapital: parseSwedishNumber(n?.EgetKap?._text),
        soliditet: n?.Soliditet?._text || '-',
        anstallda: parseSwedishNumber(n?.Anst?._text),
        kassalikviditet: n?.Kassalikv?._text || '-',
      };
    });
  } catch {
    return [];
  }
};

export interface GroupedUser {
  name: string;
  email: string;
  title: string;
  phones: string[];
  orgs: string[];
  totalRequests: number;
}

export const groupSupplierUsers = (supplierUsers: any[]): GroupedUser[] => {
  if (!supplierUsers || supplierUsers.length === 0) return [];
  const grouped = new Map<string, GroupedUser>();
  for (const u of supplierUsers) {
    const key = (u.email || '').toLowerCase();
    if (!grouped.has(key)) {
      grouped.set(key, { name: u.name, email: u.email, title: u.title || '', phones: [], orgs: [], totalRequests: 0 });
    }
    const g = grouped.get(key)!;
    g.totalRequests += u.requestCount || 0;
    if (u.orgName && !g.orgs.includes(u.orgName)) g.orgs.push(u.orgName);
    if (u.phone && !g.phones.includes(u.phone)) g.phones.push(u.phone);
    if (!g.title && u.title) g.title = u.title;
  }
  return Array.from(grouped.values()).filter(g => g.email && g.name);
};

type RouteTypes = RouteComponentProps<{}, Record<string, unknown>>;

export type SupplierCompanyCardData = {
  companyInfo: any;
  supplierStats: any;
  ratingInfo: any;
  kpiData: KPIYear[];
  groupedUsers: GroupedUser[];
  reachData: any;
  loading: boolean;
};

export const useSupplierCompanyCardData = (organizationNumber?: string): SupplierCompanyCardData => {
  const [companyData, setCompanyData] = useState<any>(null);
  const [supplierStats, setSupplierStats] = useState<any>(null);
  const [supplierUsers, setSupplierUsers] = useState<any[]>([]);
  const [reachData, setReachData] = useState<any>(null);
  const [loading, setLoading] = useState(Boolean(organizationNumber));

  useEffect(() => {
    if (!organizationNumber) return;
    let cancelled = false;
    const orgNr = organizationNumber.replace(/[-\s]/g, '');
    setLoading(true);
    setCompanyData(null);
    setSupplierStats(null);
    setSupplierUsers([]);
    setReachData(null);

    (async () => {
      try {
        const [company, stats] = await Promise.all([
          ServiceSalesTool.companyLookup(organizationNumber).catch(() => null),
          ServiceSalesTool.getSupplierStats(orgNr).catch(() => null),
        ]);
        if (cancelled) return;
        setCompanyData(company);
        setSupplierStats(stats);

        const [users, reach] = await Promise.all([
          ServiceSalesTool.getSupplierUsers(orgNr).catch(() => []),
          ServiceSalesTool.getSupplierReach(orgNr, stats?.categories || []).catch(() => null),
        ]);
        if (cancelled) return;
        setSupplierUsers(users);
        setReachData(reach);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [organizationNumber]);

  const companyInfo = useMemo(() => {
    const info = parseSynaCompanyData(companyData);
    if (info && companyData?.boardData) {
      info.board = parseSynaBoardData(companyData.boardData);
    }
    return info;
  }, [companyData]);
  const ratingInfo = useMemo(() => parseSynaRating(companyData), [companyData]);
  const kpiData = useMemo(() => parseSynaKPIs(companyData), [companyData]);
  const groupedUsers = useMemo(() => groupSupplierUsers(supplierUsers), [supplierUsers]);

  return { companyInfo, supplierStats, ratingInfo, kpiData, groupedUsers, reachData, loading };
};

type Props = {
  companyInfo: any;
  supplierStats: any;
  ratingInfo: any;
  kpiData: KPIYear[];
  groupedUsers: GroupedUser[];
  reachData: any;
  history: RouteTypes['history'];
  hasSyna?: boolean;
  hasCreditsafe?: boolean;
  hasTic?: boolean;
  // Fallback identity lets the card render its header/controls from the caller's known
  // values when the Syna lookup returns nothing.
  fallbackCompanyName?: string;
  fallbackOrganizationNumber?: string;
};

const SupplierCompanyCard: FC<Props> = ({
  companyInfo,
  supplierStats,
  ratingInfo,
  kpiData,
  groupedUsers,
  reachData,
  history,
  hasSyna = true,
  hasCreditsafe = false,
  hasTic = false,
  fallbackCompanyName,
  fallbackOrganizationNumber,
}) => {
  const [t] = useTranslation('salesTool');

  const bidRequestMap = useMemo(() => {
    const map = new Map<string, number>();
    (supplierStats?.bidRequestOrgs || []).forEach((org: any) => {
      map.set(org.orgName, org.count);
    });
    return map;
  }, [supplierStats]);

  const displayName = companyInfo?.name ?? fallbackCompanyName;
  const displayOrgNumber = companyInfo?.orgNumber ?? fallbackOrganizationNumber;
  // Prefer the fallback (stable, available immediately) for the controls so they mount on
  // first render and don't refetch when the Syna lookup resolves.
  const controlsOrgNumber = fallbackOrganizationNumber ?? supplierStats?.organizationNumber;
  const controlsCompanyName = fallbackCompanyName ?? (supplierStats?.name || companyInfo?.name);

  // Render without companyInfo only when a fallback identity is supplied (the detail-panel
  // use case). The sales-tool page passes none, so it still renders nothing until lookup resolves.
  if (!companyInfo && !fallbackOrganizationNumber) return null;

  return (
    <div className="sales-tool__company-card">
      <div className="sales-tool__company-card-header">
        <span className="sales-tool__company-card-name">{displayName}</span>
        <div className="sales-tool__company-card-badges">
          {supplierStats?.accountTypes?.includes('SUPERADMIN') && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--superadmin">{t('badge.superadmin', 'Superadmin')}</span>
          )}
          {supplierStats?.accountTypes?.includes('PURCHASING') && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--purchasing">{t('badge.purchasing', 'Inköp')}</span>
          )}
          {supplierStats?.accountTypes?.includes('SALES') && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--sales">{t('badge.sales', 'Leverantör')}</span>
          )}
          {supplierStats?.subscriptions?.map((sub: any) => (
            <span key={sub.product} className="sales-tool__company-card-badge sales-tool__company-card-badge--subscription">
              {t(`subscription.${sub.product}`, sub.product) as string}
            </span>
          ))}
          {!supplierStats?.accountTypes?.filter((type: string) => type !== 'SUPERADMIN').length &&
            !supplierStats?.accountTypes?.includes('SUPERADMIN') && supplierStats && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--none">{t('badge.noAccount', 'Ej konto')}</span>
          )}
        </div>
      </div>
      <div className="sales-tool__company-card-grid">
        <div className="sales-tool__company-card-field">
          <div className="sales-tool__company-card-field-label">{t('company.orgNumber', 'Org.nummer')}</div>
          <div className="sales-tool__company-card-field-value">{displayOrgNumber}</div>
        </div>
        {companyInfo && (
          <div className="sales-tool__company-card-field">
            <div className="sales-tool__company-card-field-label">{t('company.address', 'Adress')}</div>
            <div className="sales-tool__company-card-field-value">
              {companyInfo.address}, {companyInfo.postcode} {companyInfo.city}
            </div>
          </div>
        )}
        {companyInfo && (
          <div className="sales-tool__company-card-field">
            <div className="sales-tool__company-card-field-label">{t('company.registrationDate', 'Registreringsdatum')}</div>
            <div className="sales-tool__company-card-field-value">
              {companyInfo.regdatum?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
            </div>
          </div>
        )}
        {companyInfo?.bransch && (
          <div className="sales-tool__company-card-field">
            <div className="sales-tool__company-card-field-label">{t('company.industry', 'Bransch')}</div>
            <div className="sales-tool__company-card-field-value">{companyInfo.bransch}</div>
          </div>
        )}
        {ratingInfo && (
          <div className="sales-tool__company-card-field">
            <div className="sales-tool__company-card-field-label">{t('company.creditLimit', 'Kreditlimit')}</div>
            <div className="sales-tool__company-card-field-value">
              {Number(ratingInfo.limit).toLocaleString('sv-SE')} kr
            </div>
          </div>
        )}
      </div>

      {/* Board / Ledning & Styrelse */}
      {companyInfo?.board?.length > 0 && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('company.board', 'Ledning & Styrelse')}</h4>
          <div className="sales-tool__company-card-grid">
            {companyInfo.board.map((member: any, i: number) => (
              <div key={i} className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">{member.role}</div>
                <div className="sales-tool__company-card-field-value">{member.name}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Bokslut / Financial Statements */}
      {kpiData.length > 0 && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('company.financials.title', 'Bokslut')}</h4>
          <div className="sales-tool__bokslut">
            <div className="sales-tool__bokslut-chart">
              <BarChart
                title=""
                data={kpiData.map(k => ({
                  name: k.period,
                  [t('chart.revenueLabel', 'Omsättning')]: k.omsattning,
                  [t('chart.resultLabel', 'Resultat')]: k.resultat,
                }))}
                index="name"
                categories={[t('chart.revenueLabel', 'Omsättning'), t('chart.resultLabel', 'Resultat')]}
                colors={['emerald', 'amber']}
                unit={UNIT.AMOUNT_TKR}
              />
            </div>
            <div className="sales-tool__bokslut-table">
              <div className="sales-tool__bokslut-header">
                <span>{t('company.financials.title', 'Bokslut')}</span>
                <span>{kpiData[0]?.period}</span>
              </div>
              {[
                { label: t('company.financials.revenue', 'Omsättning'), value: kpiData[0]?.omsattning, highlight: true },
                { label: t('company.financials.result', 'Resultat efter finansnetto'), value: kpiData[0]?.resultat, highlight: true },
                { label: t('company.financials.totalAssets', 'Summa tillgångar'), value: kpiData[0]?.balansomslutning },
                { label: t('company.financials.equity', 'Eget kapital'), value: kpiData[0]?.egetKapital, highlight: true },
                { label: t('company.financials.employees', 'Anställda'), value: kpiData[0]?.anstallda, isCount: true },
                { label: t('company.financials.solidity', 'Soliditet'), value: kpiData[0]?.soliditet, isSuffix: '%' },
                { label: t('company.financials.cashLiquidity', 'Kassalikviditet'), value: kpiData[0]?.kassalikviditet, isSuffix: '%' },
              ].map((row: any, i: number) => (
                <div key={i} className={`sales-tool__bokslut-row ${row.highlight ? 'sales-tool__bokslut-row--highlight' : ''}`}>
                  <span>{row.label}</span>
                  <span>
                    {row.isSuffix
                      ? `${row.value}${row.isSuffix}`
                      : row.isCount
                        ? row.value
                        : `${Number(row.value).toLocaleString('sv-SE')} tkr`}
                  </span>
                </div>
              ))}
              <div className="sales-tool__bokslut-row sales-tool__bokslut-row--footer">
                <span>{t('company.financials.currencyNote', 'Valutakod (Belopp i 1000)')}</span>
                <span>SEK</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Supplier Stats inside company card */}
      {supplierStats && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('section.activity', 'Aktivitet på Accurator')}</h4>
          <div className="sales-tool__company-card-stats">
            {[
              {
                value: supplierStats.customerRegisters?.count || 0,
                label: t('activity.customerRegisters.label', 'Kundregister'),
                tooltip: t('activity.customerRegisters.tooltip', 'Antal aktiva kundorganisationer (exkl. demo/superadmin) som har en leverantörspost med detta organisationsnummer i sitt register.'),
              },
              {
                value: reachData?.currentCustomers ?? '-',
                label: t('activity.currentCustomers.label', 'Kunder idag'),
                tooltip: t('activity.currentCustomers.tooltip', 'Antal unika organisationer som har skickat minst en förfrågan till denna leverantör. Räknas via projektens orgId i aktiva, ej borttagna projekt.'),
              },
              {
                value: supplierStats.bidRequests?.received || 0,
                label: t('activity.receivedRequests.label', 'Mottagna förfrågningar'),
                tooltip: t('activity.receivedRequests.tooltip', 'Antal unika inköp (projectId+areaId) där leverantören finns med i suppliers-arrayen i versions-kollektionen. Räknas över alla organisationer.'),
              },
              {
                value: supplierStats.bidRequests?.submitted || 0,
                label: t('activity.submittedBids.label', 'Inlämnade anbud'),
                tooltip: t('activity.submittedBids.tooltip', 'Antal unika inköp (projectId+areaId) där leverantören har en supplierBids-post i någon version. Inkluderar bud på borttagna projekt.'),
              },
              {
                value: supplierStats.wonDeals || 0,
                label: t('activity.wonDeals.label', 'Vunna affärer'),
                tooltip: t('activity.wonDeals.tooltip', 'Antal inköp där leverantören är tilldelad (reward: true) OCH det finns ett signerat Scrive-kontrakt eller manuellt kontrakt. Enbart aktiva, ej borttagna projekt.'),
              },
              {
                value: groupedUsers.length || 0,
                label: t('activity.contacts.label', 'Kontaktpersoner'),
                tooltip: t('activity.contacts.tooltip', 'Antal unika e-postadresser bland aktiva användare (isActive: true) kopplade till leverantörens supplier-poster. Exkl. demo/superadmin-organisationer.'),
              },
            ].map(stat => (
              <TooltipContainer
                key={stat.label}
                renderReferenceComponent={(className, ref) => (
                  <div className={`sales-tool__company-card-stat ${className}`} ref={ref}>
                    <span className="sales-tool__company-card-stat-value">{stat.value}</span>
                    <span className="sales-tool__company-card-stat-label">{stat.label}</span>
                  </div>
                )}
              >
                <div style={{ maxWidth: 260, fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                  <strong style={{ display: 'block', marginBottom: 4 }}>{stat.label}</strong>
                  {stat.tooltip}
                </div>
              </TooltipContainer>
            ))}
          </div>
        </>
      )}

      {/* Supplier Controls inside company card */}
      {controlsOrgNumber && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('section.supplierControls', 'Leverantörskontroller')}</h4>
          <div className="sales-tool__company-card-controls">
            <SupplierControls
              organizationNumber={controlsOrgNumber}
              companyName={controlsCompanyName}
              hasSyna={hasSyna}
              hasCreditsafe={hasCreditsafe}
              hasTic={hasTic}
              history={history}
            />
          </div>
        </>
      )}

      {/* Customer Organizations + Users + Bid Request Orgs */}
      {supplierStats && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">
            {t('section.orgsWithSupplier', 'Organisationer med leverantören i sitt register ({{count}})', { count: supplierStats.customerRegisters?.count || 0 })}
          </h4>

          <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>{t('table.organization', 'Organisation')}</th>
                  <th>{t('table.status', 'Status')}</th>
                  <th>{t('table.requestCount', 'Antal förfrågningar')}</th>
                </tr>
              </thead>
              <tbody>
                {supplierStats.customerRegisters?.organizations?.length > 0 ? (
                  supplierStats.customerRegisters.organizations.map((org: any, i: number) => {
                    const status = org.prequalification || 'APPROVED';
                    const label = status === 'APPROVED' ? t('status.approved', 'Godkänd') : status === 'REJECTED' ? t('status.rejected', 'Nekad') : t('status.pending', 'Ansökan');
                    const badgeClass = status === 'APPROVED' ? 'sales-tool__status-badge--approved'
                      : status === 'REJECTED' ? 'sales-tool__status-badge--rejected'
                      : 'sales-tool__status-badge--pending';
                    const bidCount = bidRequestMap.get(org.orgName);
                    return (
                      <tr key={i}>
                        <td>{org.orgName}</td>
                        <td><span className={`sales-tool__status-badge ${badgeClass}`}>{label}</span></td>
                        <td>{bidCount != null ? `${bidCount} ${t('count.suffix', 'st')}` : '–'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                    {t('empty.noOrgsWithSupplier', 'Inga organisationer har denna leverantör i sitt register.')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Users as subsection — grouped by email */}
          <h4 className="sales-tool__company-card-subtitle">
            {t('section.contacts', 'Kontaktpersoner ({{count}})', { count: groupedUsers.length })}
            <TooltipContainer
              renderReferenceComponent={(className, ref) => (
                <span ref={ref} className={className} style={{ marginLeft: 6, cursor: 'help', verticalAlign: 'middle', display: 'inline-flex' }}><InfoIcon /></span>
              )}
            >
              <div style={{ maxWidth: 260, fontSize: 13, padding: '4px 0' }}>
                {t('contacts.tooltip', 'Kontaktpersoner som finns hos Accurator-användares register')}
              </div>
            </TooltipContainer>
          </h4>
          <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>{t('table.name', 'Namn')}</th>
                  <th>{t('table.email', 'E-post')}</th>
                  <th>{t('table.phone', 'Telefon')}</th>
                  <th>{t('table.title', 'Titel')}</th>
                  <th>{t('table.requests', 'Förfrågningar')}</th>
                </tr>
              </thead>
              <tbody>
                {groupedUsers.length > 0 ? (
                  groupedUsers.map((user, i) => (
                    <TooltipContainer
                      key={i}
                      renderReferenceComponent={(className, ref) => (
                        <tr className={className} ref={ref}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            {user.phones[0] || '-'}
                            {user.phones.length > 1 && (
                              <TooltipContainer
                                renderReferenceComponent={(cn, r) => (
                                  <span className={cn} ref={r} style={{ marginLeft: 4, cursor: 'help', verticalAlign: 'middle', display: 'inline-flex' }}>
                                    <InfoIcon />
                                  </span>
                                )}
                              >
                                <div style={{ fontSize: 13, padding: '4px 0' }}>
                                  <strong>{t('contacts.otherPhones', 'Övriga telefonnummer:')}</strong>
                                  {user.phones.slice(1).map((p, j) => <div key={j}>{p}</div>)}
                                </div>
                              </TooltipContainer>
                            )}
                          </td>
                          <td>{user.title || '-'}</td>
                          <td>{user.totalRequests} {t('count.suffix', 'st')}</td>
                        </tr>
                      )}
                    >
                      {user.orgs.length > 0 && (
                        <div style={{ fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                          <strong>{user.orgs.length === 1 ? t('contacts.organization', 'Organisation:') : t('contacts.organizations', 'Organisationer:')}</strong>
                          {user.orgs.map((org, j) => <div key={j}>{org}</div>)}
                        </div>
                      )}
                    </TooltipContainer>
                  ))
                ) : (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                    {t('empty.noUsers', 'Inga registrerade användare hos leverantören.')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Show orgs that sent bids but are NOT in the register */}
          {supplierStats.bidRequestOrgs?.some((org: any) =>
            !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName)
          ) && (
            <div className="sales-tool__supplier-users" style={{ marginTop: -8 }}>
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={{ fontWeight: 500, fontSize: 13, color: 'var(--gray-500)', paddingTop: 4 }}>
                      {t('table.otherOrgsWithRequests', 'Övriga organisationer som skickat förfrågningar')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {supplierStats.bidRequestOrgs
                    .filter((org: any) => !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName))
                    .map((org: any, i: number) => (
                      <tr key={i}>
                        <td>{org.orgName}</td>
                        <td>–</td>
                        <td>{org.count} {t('count.suffix', 'st')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SupplierCompanyCard;
